import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  downloadPortalCertificate,
} from "./useStudentPortalFetch";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";

function displayValue(value) {
  if (value == null || String(value).trim() === "") return null;
  return String(value).trim();
}

function resolveStudentPhotoUrl(value) {
  if (value == null || String(value).trim() === "") return null;
  let s = String(value).trim();

  s = s.replace(/\/storage\/students\//i, "/storage/app/public/students/");

  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith("/")) return `${MAIN_URL}${s}`;
  if (s.startsWith("storage/app/public/") || s.startsWith("students/")) {
    const path = s.startsWith("students/")
      ? `storage/app/public/${s}`
      : s;
    return `${MAIN_URL}/${path.replace(/^\/+/, "")}`;
  }
  if (s.startsWith("storage/")) {
    return `${MAIN_URL}/storage/app/public/${s.replace(/^storage\//, "")}`;
  }
  return `${MAIN_URL}/storage/app/public/students/${s.replace(/^\/+/, "")}`;
}

function resolveLocationId(entity, keys = []) {
  if (!entity) return "";
  for (const key of keys) {
    const value = entity[key];
    if (value != null && value !== "") return String(value);
  }
  return "";
}

function buildFormFromProfile(profile) {
  if (!profile) {
    return {
      phone: "",
      address: "",
      country_id: "",
      state_id: "",
      city_id: "",
    };
  }

  return {
    phone: profile.phone || "",
    address: profile.address || "",
    country_id: resolveLocationId(profile, ["country_id"]) ||
      resolveLocationId(profile.country, [
        "general_country_id",
        "country_id",
        "organization_general_country_id",
      ]),
    state_id: resolveLocationId(profile, ["state_id"]) ||
      resolveLocationId(profile.state, [
        "general_state_id",
        "state_id",
        "organization_general_state_id",
      ]),
    city_id: resolveLocationId(profile, ["city_id"]) ||
      resolveLocationId(profile.city, [
        "general_city_id",
        "city_id",
        "organization_general_city_id",
      ]),
  };
}

function Field({ icon, label, value, editing, children }) {
  const shown = displayValue(value);
  return (
    <div className="ems-profile-field">
      <div className="ems-profile-field-label">
        {icon ? <span className="ems-profile-field-icon">{icon}</span> : null}
        <span>{label}</span>
      </div>
      {editing && children ? (
        children
      ) : (
        <div
          className={`ems-profile-field-value${
            shown ? "" : " ems-profile-field-empty"
          }`}
        >
          {shown || "Not Provided"}
        </div>
      )}
    </div>
  );
}

function IconMail() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5L4 8V6l8 5 8-5v2z"
      />
    </svg>
  );
}

function IconPhone() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1.1-.3 1.2.4 2.5.6 3.8.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.6.6 3.8.1.4 0 .8-.3 1.1L6.6 10.8z"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8V22h19.2v-2.8c0-3.2-6.4-4.8-9.6-4.8z"
      />
    </svg>
  );
}

function IconCake() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 6c.8 0 1.5-.7 1.5-1.5S12.8 3 12 3s-1.5.7-1.5 1.5S11.2 6 12 6zm6 4h-2V8h-2v2h-4V8H8v2H6c-1.1 0-2 .9-2 2v2h16v-2c0-1.1-.9-2-2-2zm2 5H4v5c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-5z"
      />
    </svg>
  );
}

function IconId() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM8.5 15.5c-1.4 0-2.5-1.1-2.5-2.5s1.1-2.5 2.5-2.5S11 11.6 11 13s-1.1 2.5-2.5 2.5zM18 15h-5v-1.5h5V15zm0-3.5h-5V10h5v1.5z"
      />
    </svg>
  );
}

function IconPin() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z"
      />
    </svg>
  );
}

function IconCity() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M15 11V5l-3-3-3 3v2H3v14h18V11h-6zM7 19H5v-2h2v2zm0-4H5v-2h2v2zm0-4H5V9h2v2zm6 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V9h2v2zm0-4h-2V5h2v2zm6 12h-2v-2h2v2zm0-4h-2v-2h2v2z"
      />
    </svg>
  );
}

function initials(firstName, lastName) {
  const a = String(firstName || "").trim().charAt(0);
  const b = String(lastName || "").trim().charAt(0);
  return `${a}${b}`.toUpperCase() || "?";
}

export default function StudentProfilePage() {
  const { data, loading, error, setData } = useStudentPortalFetch("me");
  const [photoFailed, setPhotoFailed] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(buildFormFromProfile(null));
  const [formErrors, setFormErrors] = useState({});
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const fullName = [data?.first_name, data?.last_name]
    .filter(Boolean)
    .join(" ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const photoUrl = resolveStudentPhotoUrl(data?.profile_image_url);
  const showPhoto = Boolean(photoUrl) && !photoFailed;
  const city = data?.city?.city_name || data?.city?.name;
  const state = data?.state?.state_name || data?.state?.name;
  const country = data?.country?.country_name || data?.country?.name;
  const certificates = Array.isArray(data?.certificates)
    ? data.certificates
    : [];

  useEffect(() => {
    setPhotoFailed(false);
  }, [photoUrl]);

  useEffect(() => {
    if (!isEditing) {
      setForm(buildFormFromProfile(data));
    }
  }, [data, isEditing]);

  const loadCountries = useCallback(async () => {
    const res = await axios.get(`${MAIN_URL}/api/general/countries/v1`, {
      headers: getApiHeaders(),
    });
    const rows = Array.isArray(res.data)
      ? res.data
      : res.data?.data || res.data?.countries || [];
    setCountries(rows);
  }, []);

  const loadStates = useCallback(async (countryId) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    const res = await axios.get(
      `${MAIN_URL}/api/general/countries/${countryId}/states/v1`,
      { headers: getApiHeaders() }
    );
    const rows = Array.isArray(res.data)
      ? res.data
      : res.data?.data || res.data?.states || [];
    setStates(rows);
  }, []);

  const loadCities = useCallback(async (countryId, stateId) => {
    if (!countryId || !stateId) {
      setCities([]);
      return;
    }
    const res = await axios.get(
      `${MAIN_URL}/api/general/countries/${countryId}/states/${stateId}/cities`,
      { headers: getApiHeaders() }
    );
    const rows = Array.isArray(res.data)
      ? res.data
      : res.data?.data || res.data?.cities || [];
    setCities(rows);
  }, []);

  useEffect(() => {
    if (!isEditing) return;

    let cancelled = false;
    const boot = async () => {
      setLoadingLocations(true);
      try {
        const initial = buildFormFromProfile(data);
        await loadCountries();
        if (initial.country_id) {
          await loadStates(initial.country_id);
        }
        if (initial.country_id && initial.state_id) {
          await loadCities(initial.country_id, initial.state_id);
        }
      } catch {
        if (!cancelled) {
          toast.error("Could not load location options.");
        }
      } finally {
        if (!cancelled) setLoadingLocations(false);
      }
    };

    boot();
    return () => {
      cancelled = true;
    };
  }, [isEditing, data, loadCountries, loadStates, loadCities]);

  const handleFormChange = (name, value) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "country_id") {
        next.state_id = "";
        next.city_id = "";
      }
      if (name === "state_id") {
        next.city_id = "";
      }
      return next;
    });
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleStartEdit = () => {
    setForm(buildFormFromProfile(data));
    setFormErrors({});
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setForm(buildFormFromProfile(data));
    setFormErrors({});
    setIsEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setFormErrors({});
    try {
      const payload = {
        phone: String(form.phone || "").trim(),
        address: String(form.address || "").trim(),
        country_id: form.country_id ? Number(form.country_id) : null,
        state_id: form.state_id ? Number(form.state_id) : null,
        city_id: form.city_id ? Number(form.city_id) : null,
      };

      const res = await axios.put(`${MAIN_URL}/api/ems/portal/me`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data?.data) {
        setData(res.data.data);
      }
      toast.success(res.data?.message || "Profile updated successfully.");
      setIsEditing(false);
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors && typeof apiErrors === "object") {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, messages]) => {
          mapped[key] = Array.isArray(messages) ? messages[0] : messages;
        });
        setFormErrors(mapped);
      }
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Could not update profile."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadCertificate = async (row) => {
    const certificateId = row?.organization_ems_certificate_id;
    if (!certificateId || !row?.can_download) return;

    setDownloadError("");
    setDownloadingId(certificateId);
    try {
      await downloadPortalCertificate(
        certificateId,
        row.certificate_number || row.certificate_name || "certificate",
        row.certificate_url
      );
    } catch (err) {
      setDownloadError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not download certificate."
      );
    } finally {
      setDownloadingId(null);
    }
  };

  const countryOptions = useMemo(
    () =>
      countries.map((row) => ({
        id: String(
          row.general_country_id ??
            row.country_id ??
            row.organization_general_country_id ??
            ""
        ),
        label:
          row.country_name ||
          row.name ||
          row.general_country_name ||
          "Country",
      })),
    [countries]
  );

  const stateOptions = useMemo(
    () =>
      states.map((row) => ({
        id: String(
          row.general_state_id ??
            row.state_id ??
            row.organization_general_state_id ??
            ""
        ),
        label: row.state_name || row.name || row.general_state_name || "State",
      })),
    [states]
  );

  const cityOptions = useMemo(
    () =>
      cities.map((row) => ({
        id: String(
          row.general_city_id ??
            row.city_id ??
            row.organization_general_city_id ??
            ""
        ),
        label: row.city_name || row.name || row.general_city_name || "City",
      })),
    [cities]
  );

  const selectedCountryLabel =
    countryOptions.find((row) => row.id === String(form.country_id))?.label ||
    country;
  const selectedStateLabel =
    stateOptions.find((row) => row.id === String(form.state_id))?.label ||
    state;
  const selectedCityLabel =
    cityOptions.find((row) => row.id === String(form.city_id))?.label || city;

  return (
    <StudentPortalShell
      title="My Profile"
      headerRight={
        !loading && data ? (
          isEditing ? (
            <div className="ems-profile-actions">
              <button
                type="button"
                className="ems-student-btn ems-student-btn-ghost"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ems-student-btn ems-student-btn-primary ems-profile-edit-btn"
                onClick={handleSave}
                disabled={saving || loadingLocations}
              >
                {saving ? "Saving…" : "Save Changes"}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="ems-student-btn ems-student-btn-primary ems-profile-edit-btn"
              onClick={handleStartEdit}
            >
              Edit Profile
            </button>
          )
        ) : null
      }
    >
      <StudentPortalState loading={loading} error={error} empty={!data}>
        <div className="ems-student-profile ems-profile-modern">
          {isEditing ? (
            <div className="ems-profile-edit-notice">
              <p>
                You can update your phone number and address details. Student
                ID, name, email, gender, and date of birth are managed by the
                institute.
              </p>
            </div>
          ) : null}

          <header className="ems-profile-hero-card">
            <div className="ems-student-photo-wrap">
              {showPhoto ? (
                <img
                  className="ems-student-photo"
                  src={photoUrl}
                  alt={fullName || "Student"}
                  onError={() => setPhotoFailed(true)}
                />
              ) : null}
              <div
                className="ems-student-photo-fallback"
                style={showPhoto ? { display: "none" } : undefined}
              >
                {initials(data?.first_name, data?.last_name)}
              </div>
            </div>
            <div className="ems-profile-hero-copy">
              <h2 className="ems-student-profile-name">
                {fullName || "Not Provided"}
              </h2>
              <p className="ems-profile-hero-id">
                {displayValue(data?.student_id) || "Not Provided"}
              </p>
            </div>
          </header>

          <section className="ems-profile-card">
            <div className="ems-profile-card-header">
              <h3 className="ems-profile-card-title">Personal Information</h3>
            </div>
            <div className="ems-profile-grid">
              <Field
                icon={<IconId />}
                label="Student ID"
                value={data?.student_id}
              />
              <Field icon={<IconUser />} label="Full Name" value={fullName} />
              <Field icon={<IconMail />} label="Email" value={data?.email} />
              <Field
                icon={<IconPhone />}
                label="Phone"
                value={isEditing ? form.phone : data?.phone}
                editing={isEditing}
              >
                <input
                  className="ems-profile-input"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => handleFormChange("phone", e.target.value)}
                  placeholder="Enter phone number"
                />
                {formErrors.phone ? (
                  <span className="ems-profile-field-error">{formErrors.phone}</span>
                ) : null}
              </Field>
              <Field icon={<IconUser />} label="Gender" value={data?.gender} />
              <Field
                icon={<IconCake />}
                label="Date of Birth"
                value={formatPortalDate(data?.date_of_birth)}
              />
            </div>
          </section>

          <section className="ems-profile-card">
            <div className="ems-profile-card-header">
              <h3 className="ems-profile-card-title">Address Details</h3>
            </div>
            <div className="ems-profile-grid">
              <Field
                icon={<IconPin />}
                label="Address"
                value={isEditing ? form.address : data?.address}
                editing={isEditing}
              >
                <textarea
                  className="ems-profile-input ems-profile-textarea"
                  rows={3}
                  value={form.address}
                  onChange={(e) => handleFormChange("address", e.target.value)}
                  placeholder="Enter your address"
                />
                {formErrors.address ? (
                  <span className="ems-profile-field-error">
                    {formErrors.address}
                  </span>
                ) : null}
              </Field>
              <Field
                icon={<IconCity />}
                label="City"
                value={isEditing ? selectedCityLabel : city}
                editing={isEditing}
              >
                <select
                  className="ems-profile-input"
                  value={form.city_id}
                  disabled={!form.state_id || loadingLocations}
                  onChange={(e) => handleFormChange("city_id", e.target.value)}
                >
                  <option value="">Select city</option>
                  {cityOptions.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                icon={<IconPin />}
                label="State"
                value={isEditing ? selectedStateLabel : state}
                editing={isEditing}
              >
                <select
                  className="ems-profile-input"
                  value={form.state_id}
                  disabled={!form.country_id || loadingLocations}
                  onChange={async (e) => {
                    const nextStateId = e.target.value;
                    handleFormChange("state_id", nextStateId);
                    if (form.country_id && nextStateId) {
                      await loadCities(form.country_id, nextStateId);
                    } else {
                      setCities([]);
                    }
                  }}
                >
                  <option value="">Select state</option>
                  {stateOptions.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                icon={<IconPin />}
                label="Country"
                value={isEditing ? selectedCountryLabel : country}
                editing={isEditing}
              >
                <select
                  className="ems-profile-input"
                  value={form.country_id}
                  disabled={loadingLocations}
                  onChange={async (e) => {
                    const nextCountryId = e.target.value;
                    handleFormChange("country_id", nextCountryId);
                    if (nextCountryId) {
                      await loadStates(nextCountryId);
                    } else {
                      setStates([]);
                      setCities([]);
                    }
                  }}
                >
                  <option value="">Select country</option>
                  {countryOptions.map((row) => (
                    <option key={row.id} value={row.id}>
                      {row.label}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </section>

          <section className="ems-profile-card">
            <div className="ems-profile-card-header">
              <h3 className="ems-profile-card-title">Certificates</h3>
            </div>
            {certificates.length === 0 ? (
              <p className="ems-profile-field-empty" style={{ margin: 0 }}>
                No certificates issued yet.
              </p>
            ) : (
              <div className="ems-student-table-wrap">
                {downloadError ? (
                  <p className="ems-student-portal-error" style={{ marginBottom: 12 }}>
                    {downloadError}
                  </p>
                ) : null}
                <table className="ems-student-table ems-profile-certificate-table">
                  <thead>
                    <tr>
                      <th>Certificate</th>
                      <th>Program</th>
                      <th>Number</th>
                      <th>Issue Date</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {certificates.map((row) => {
                      const title =
                        row.certificate_name ||
                        row.training_program_name ||
                        "Certificate";
                      const isDownloading =
                        downloadingId === row.organization_ems_certificate_id;

                      return (
                        <tr key={row.organization_ems_certificate_id}>
                          <td>
                            <strong>{title}</strong>
                          </td>
                          <td>{row.training_program_name || "—"}</td>
                          <td>{row.certificate_number || "—"}</td>
                          <td>
                            {formatPortalDate(row.issue_date) || "—"}
                          </td>
                          <td>{row.certificate_status || "—"}</td>
                          <td>
                            {row.can_download ? (
                              <button
                                type="button"
                                className="ems-student-btn ems-student-btn-primary"
                                disabled={isDownloading}
                                onClick={() => handleDownloadCertificate(row)}
                              >
                                {isDownloading ? "Downloading…" : "Download"}
                              </button>
                            ) : (
                              <span className="ems-student-meta">
                                Not available
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </StudentPortalState>
    </StudentPortalShell>
  );
}
